/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import type { MutableRef } from 'preact/hooks';
import type { Item, ListItemContext as ListItemMenuContext } from '#UNSAFE_Collection';
import type { CollectionData } from '#PRIVATE_TableCommon/Common.types';
import type { Cell } from '../List.types';
import type { ActiveCell, ActiveCellDetail, CurrencyStatus, ViewportInfo } from '../List';

import { getElementFromCell, getRowIndexForRowKey, isAutoFocusCell } from './ListDomUtils';
import { applyScrollDetail, resetScrollPosition } from './ListScrollUtils';

/**
 * Helper function to determine if a given active cell represents a 'transient' state.
 */
export function isTransientCell<K>(activeCell?: ActiveCell<K>) {
  return activeCell != null && (activeCell.type === 'loadMore' || activeCell.type === 'loading');
}

/**
 * Helper function to compare two given cell descriptors.
 */
export function cellComparator<K>(
  cell1?: ActiveCell<K> | Cell<K>,
  cell2?: ActiveCell<K> | Cell<K>
) {
  if (cell1 == null) {
    return cell2 == null;
  }
  if (cell2 == null) {
    return false;
  }
  return cell1.type === cell2.type && cell1.rowKey === cell2.rowKey;
}

export function getCellContextMenuContextFromCell<K extends string | number, D>(
  rootElement: HTMLElement,
  listId: string,
  listData: CollectionData<K, D>[],
  cell?: ActiveCell<K>
) {
  if (cell?.type === 'data') {
    const allMenuItems = {};
    const defaultMenuItems = undefined;
    for (const rowData of listData) {
      if (rowData.metadata.key === cell.rowKey) {
        return {
          allMenuItems,
          defaultMenuItems,
          data: rowData.data,
          metadata: { key: cell.rowKey },
          index: getRowIndexForRowKey(rootElement, listId, cell.rowKey)
        } as ListItemMenuContext<K, D>;
      }
    }
  }
  return undefined;
}

/**
 * Helper function to determine the resulting cell from a cell override.
 */
function _getCellFromCellOverride<K>(cellOverride: Item<K>): Cell<K> | undefined {
  let cellType;
  let cellRowKey;
  if (cellOverride?.rowKey != null) {
    cellType = 'data';
    cellRowKey = cellOverride.rowKey;
  } else {
    return undefined;
  }
  return { type: cellType, rowKey: cellRowKey } as Cell<K>;
}

/**
 * Helper function to determine the resulting cell type from a cell override.
 */
function _getCellTypeFromCellOverride<K>(cellOverride: Item<K>) {
  if (cellOverride?.rowKey != null) {
    return 'data';
  }
  return 'data';
}

/**
 * Helper function to apply a current cell override if necessary.
 */
export function applyCurrentCellOverride<K extends string | number, D>(
  rootElement: HTMLDivElement,
  scrollerElement: HTMLElement,
  viewportInfo: ViewportInfo,
  listId: string,
  currencyStatusRef: MutableRef<CurrencyStatus<K, D>>,
  onActiveCellChanged: (
    detail: ActiveCellDetail<K>,
    isAutoFocus?: boolean,
    isSelectionExtension?: boolean,
    skipScrollCellIntoView?: boolean
  ) => void,
  isEmbeddedSelect: boolean,
  isDataLoaded: boolean,
  currentItemOverride?: Item<K>
) {
  // ensure any new currentItemOverride settings are applied if necessary
  if (
    currencyStatusRef.current.hasPendingCurrentCellOverride ||
    currencyStatusRef.current.currentItemOverride !== currentItemOverride
  ) {
    if (currentItemOverride != null) {
      // attempt to find the cell the override specifies
      const realizedCell = _getCellFromCellOverride<K>(currentItemOverride);
      if (realizedCell != null) {
        // override cell was determined - apply the override immediately
        onActiveCellChanged(
          { value: realizedCell },
          isAutoFocusCell(rootElement, listId, realizedCell),
          false,
          true
        );
        currencyStatusRef.current.currentItemOverride = currentItemOverride;
        currencyStatusRef.current.hasPendingCurrentCellOverride = false;
        if (isEmbeddedSelect) {
          // scroll 'current-cell-override' vertically into view immediately for embedded select
          // otherwise, cell will be scrolled into view once the component or cell receives focus
          if (getElementFromCell(rootElement, listId, realizedCell) != null) {
            applyScrollDetail(rootElement, scrollerElement, viewportInfo, listId, {
              cell: realizedCell,
              locationY: 'inView'
            });
            currencyStatusRef.current.hasPendingCurrentCellOverrideScroll = false;
          } else if (!isDataLoaded) {
            // corresponding element was not found - data is not ready for scrolling
            currencyStatusRef.current.hasPendingCurrentCellOverrideScroll = true;
          } else {
            // corresponding element was not found - value was garbage
            currencyStatusRef.current.hasPendingCurrentCellOverrideScroll = false;
          }
        } else {
          // not in an embedded select, so no scrolling should be performed
          currencyStatusRef.current.hasPendingCurrentCellOverrideScroll = false;
        }
      } else if (!isDataLoaded) {
        // override cell could not be determined - data is still fetching
        currencyStatusRef.current.hasPendingCurrentCellOverride = true;
      } else {
        // override cell could not be determined - value was garbage
        currencyStatusRef.current.currentItemOverride = currentItemOverride;
        currencyStatusRef.current.hasPendingCurrentCellOverride = false;
        currencyStatusRef.current.hasPendingCurrentCellOverrideScroll = false;
      }
    } else {
      // override is set to clear the current cell
      onActiveCellChanged({ value: currentItemOverride });
      currencyStatusRef.current.currentItemOverride = currentItemOverride;
      currencyStatusRef.current.hasPendingCurrentCellOverride = false;
      if (isEmbeddedSelect) {
        // scroll back to the top for embedded select
        resetScrollPosition(scrollerElement);
      }
      currencyStatusRef.current.hasPendingCurrentCellOverrideScroll = false;
    }
  } else if (currencyStatusRef.current.hasPendingCurrentCellOverrideScroll) {
    // try and apply any pending scroll from a previous cell override at this time
    if (currencyStatusRef.current.currentCell != null) {
      if (getElementFromCell(rootElement, listId, currencyStatusRef.current.currentCell) != null) {
        applyScrollDetail(rootElement, scrollerElement, viewportInfo, listId, {
          cell: currencyStatusRef.current.currentCell,
          locationY: 'inView'
        });
        currencyStatusRef.current.hasPendingCurrentCellOverrideScroll = false;
      } else if (isDataLoaded) {
        // corresponding element was not found - value was garbage
        currencyStatusRef.current.hasPendingCurrentCellOverrideScroll = false;
      }
    } else {
      // there is no longer a current cell to try and scroll to
      currencyStatusRef.current.hasPendingCurrentCellOverrideScroll = false;
    }
  }
}

/**
 * Helper to determine if the given render state includes the cell specified.
 */
function _renderIncludesCell<K, D>(
  cell: ActiveCell<K>,
  listData: CollectionData<K, D>[],
  hasMore: boolean
) {
  if (listData.length > 0) {
    if (cell.type === 'loadMore') {
      return hasMore;
    }
    if (cell.type === 'data') {
      for (let i = 0; i < listData.length; i++) {
        if (listData[i].metadata.key === cell.rowKey) {
          return true;
        }
      }
    }
  } else {
    return (cell.type === 'loading' && hasMore) || (cell.type === 'noData' && !hasMore);
  }
  return false;
}

/**
 * Helper to get the first cell of a given render.
 */
function _getFirstCell<K, D>(listData: CollectionData<K, D>[]): ActiveCell<K> | undefined {
  if (listData.length > 0) {
    return { rowKey: listData[0].metadata.key, type: 'data' };
  }
  return { type: 'loading' };
}

/**
 * Helper to get the cell that should currently be a tab stop for the given render state.
 */
export function determineActiveCell<K, D>(
  currencyStatusRef: MutableRef<CurrencyStatus<K, D>>,
  listData: CollectionData<K, D>[],
  hasMore: boolean,
  isEmbeddedSelect: boolean,
  currentItemOverride?: Item<K>
): ActiveCell<K> | undefined {
  // when embedded select, use most recent non-null current cell value as override base
  const currentCell = isEmbeddedSelect
    ? currencyStatusRef.current.previousEmbeddedSelectCurrentCell
    : currencyStatusRef.current.currentCell;
  if (
    currencyStatusRef.current.hasPendingCurrentCellOverride ||
    currencyStatusRef.current.currentItemOverride !== currentItemOverride
  ) {
    if (currentItemOverride != null) {
      // attempt to find the cell the override specifies
      const realizedCell = _getCellFromCellOverride<K>(currentItemOverride);
      if (realizedCell != null && _renderIncludesCell(realizedCell, listData, hasMore)) {
        return realizedCell;
      }
      // if here, the resulting cell from the override could not be determined or was not valid
      const overrideCellType = _getCellTypeFromCellOverride(currentItemOverride);
      if (overrideCellType === 'data') {
        if (listData.length === 0 && hasMore) {
          // pending 'data' override when 'loading' skeletons are shown
          return { type: 'loading' };
        }
      } else if (overrideCellType === 'noData' && listData.length === 0 && hasMore) {
        // pending 'noData' override when 'loading' skeletons are shown
        return { type: 'loading' };
      }
    }
    return _getFirstCell(listData);
  }

  // if here, there is no current cell override that can be applied during this render
  const activeCell = currencyStatusRef.current.activeCell;
  if (activeCell != null) {
    if (activeCell.type === 'loadMore') {
      const initialData = currencyStatusRef.current.loadMoreInitialData;
      // if loadMore skeletons are no longer shown, or the data has changed, shift to
      // the next row in the data set if possible - otherwise just the closest row
      if (listData.length > 0 && listData !== initialData) {
        const rowKey =
          initialData != null && listData.length > initialData.length
            ? listData[initialData.length].metadata.key
            : listData[listData.length - 1].metadata.key;
        const cell: ActiveCell<K> = { type: 'data', rowKey: rowKey };
        if (_renderIncludesCell(cell, listData, hasMore)) {
          return cell;
        }
      } else if (listData.length === 0) {
        return hasMore ? { type: 'loading' } : { type: 'noData' };
      }
    }
    if (_renderIncludesCell(activeCell, listData, hasMore)) {
      return activeCell;
    } else if (activeCell.type === 'loading') {
      if (currentCell != null && (currentCell.type === 'data' || currentCell.type === 'noData')) {
        // if the current cell is already set to 'data' or 'noData', and is now available, return it
        if (_renderIncludesCell(currentCell, listData, hasMore)) {
          return currentCell;
        }
      }
      // otherwise, find the first row if available
      if (listData.length > 0) {
        const rowKey = listData[0].metadata.key;
        const cell: ActiveCell<K> = { type: 'data', rowKey: rowKey };
        if (_renderIncludesCell(cell, listData, hasMore)) {
          return cell;
        }
      } else {
        return { type: 'noData' };
      }
    }
  }

  // if here, there is no active cell, or the active cell is no longer valid
  if (currentCell != null) {
    if (_renderIncludesCell(currentCell, listData, hasMore)) {
      return currentCell;
    }
    if (currentCell.type === 'data') {
      if (listData.length === 0 && hasMore) {
        // pending 'data' override when 'loading' skeletons are shown
        return { type: 'loading' };
      }
    } else if (currentCell.type === 'noData' && listData.length === 0 && hasMore) {
      // pending 'noData' override when 'loading' skeletons are shown
      return { type: 'loading' };
    }
  }
  if (listData.length === 0) {
    return hasMore ? { type: 'loading' } : { type: 'noData' };
  }
  return _getFirstCell(listData);
}
